import { ToolsColumn } from "components/features/tools/ToolsColumn"
import { ToolsContainer } from "components/features/tools/ToolsContainer"
import { Input } from "components/ui/inputs/Input"
import styled, { useTheme } from "styled-components"
import { DropdownMenu, DropdownOption } from "components/ui/inputs/DropdownMenu";
import { calculationLimits } from "store/calculation.config";
import { Button } from "components/ui/controls/Button";
import { useEffect, useMemo, useState } from "react";
import { useErrors } from "providers/ErrorsProvider";
import { calculateSuperelevation } from "./Superelevation.calculate";
import { parseHorizontalGeometry } from "utilities/geometricRoadutilities/parseHorizontalGeometry";
import { Table } from "components/ui/display/Table";
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, XAxis, YAxis } from "recharts";
import { generateTicks } from "utilities/generateTicks";
import { MaterialIcon } from "components/ui/shared/MaterialIcon";

export const Superelevation = () => {
    const GlobalTheme = useTheme();
    const {setError, unsetError} = useErrors();
    const [rawInput, setRawInput] = useState({
        speedlimit: '',
        laneWidth: '',
    });

    const [calculationInput, setCalculationInput] = useState({
        designClass: '',
        speedlimit: '',
        laneWidth: '',
        horizontalGeometry: null
    });

    const [calculatedState, setCalculatedState] = useState({
        input: null,
        derived: null,
        output: null,
        errorsToSet: [],
        errorsToUnset: []
    });

    useEffect(() => {
        const context = calculateSuperelevation(calculationInput);

        if(context.wasNormalized)
        {
            setRawInput(context.normalizedInput);
            setCalculationInput(prev => ({
                ...prev,
                ...Object.fromEntries(
                    Object.entries(context.normalizedInput).map(([key, value]) => [
                    key,
                    typeof value === 'number' ? String(value) : value,
                    ])
                ),
            }));
        }

        setCalculatedState({
            input: context.input,
            derived: context.derived,
            output: context.output
        });

        context.errorsToSet.forEach(setError);
        context.errorsToUnset.forEach(error => {
            if(!context.errorsToSet.includes(error)) unsetError(error);
        });

    }, [calculationInput, setError, unsetError]);

    const pasteHandler = async () => {
        const clipboard = await navigator.clipboard.readText();
        const parsed = parseHorizontalGeometry(clipboard);
        if(!parsed)
        {
            setError('TOOLS_HORIZONTAL_GEOMETRY_DATA_INVALID');
            setCalculationInput(prev => ({...prev, horizontalGeometry: null}));
        }
        else
        {
            unsetError('TOOLS_HORIZONTAL_GEOMETRY_DATA_INVALID');
            setCalculationInput(prev => ({...prev, horizontalGeometry: parsed}));
        }
    }

    const XAxisTicks = useMemo(() => {
        if(!calculatedState.output?.minStationing?.valid || !calculatedState.output?.maxStationing?.valid) return null;

        return generateTicks({
            min: calculatedState.output.minStationing.value,
            max: calculatedState.output.maxStationing.value,
            targetTicks: 10
        });
    }, [calculatedState]);

    const YAxisTicks = useMemo(() => {
        return generateTicks({
            min: -0.12,
            max: 0.12,
            step: 0.02
        });
    }, []);

    const copyToClipboard = (content) => {
        let resultText = "";
        content.forEach(row => {
            let rowText = row.stationing.toFixed(3) + "\t" + calculatedState.input.laneWidth.value.toFixed(3) + "\t" + row.crossSlope.toFixed(3);
            resultText += rowText + "\n";
        });
        
        navigator.clipboard.writeText(resultText.trim());
        setError("TOOLS_TABLE_COPIED_TO_CLIPBOARD");
    }
    
    return (
        <StyledSuperelevation>
            <ToolsColumn className="inputColumn">
                <ToolsContainer title='Inndata'>
                    <DropdownMenu
                        label="Dimensjoneringsklasse"
                        defaultValue={null}
                        defaultIcon={<MaterialIcon name='road' weight={300}/>}
                        defaultName='Velg dimensjoneringsklasse'
                        onSelect={(e) => setCalculationInput(prev => ({...prev, designClass: e}))}
                    >
                        <DropdownOption value='h1-80' name='H1 (80km/t) - Nasjonal hovedveg'/>
                        <DropdownOption value='h1-90' name='H1 (90km/t) - Nasjonal hovedveg'/>
                        <DropdownOption value='h2-90' name='H2 (90km/t) - Nasjonal hovedveg'/>
                        <DropdownOption value='h3-90' name='H3 (90km/t) - Nasjonal hovedveg'/>
                        <DropdownOption value='h3-100' name='H3 (100km/t) - Nasjonal hovedveg'/>
                        <DropdownOption value='h3-110' name='H3 (110km/t) - Nasjonal hovedveg'/>
                    </DropdownMenu>
                    <Input
                        title="Fartsgrense"
                        prefix={<span>v</span>}
                        sufix='km/t'
                        type='number'
                        min={calculationLimits.minSpeed}
                        max={calculationLimits.maxSpeed}
                        error={calculatedState.input?.speedlimit?.severity === 'error' ?? null}
                        warning={calculatedState.input?.speedlimit?.severity === 'warning' ?? null}
                        value={rawInput.speedlimit}
                        onChange={(e) => setRawInput(prev => ({...prev, speedlimit: e.target.value}))}
                        onBlur={(e) => setCalculationInput(prev => ({...prev, speedlimit: e.target.value}))}
                    />
                    <Input
                        title="Kjørefeltbredde"
                        prefix={<span>b<sub>felt</sub></span>}
                        sufix='m'
                        type='number'
                        error={calculatedState.input?.laneWidth?.severity === 'error' ?? null}
                        warning={calculatedState.input?.laneWidth?.severity === 'warning' ?? null}
                        value={rawInput.laneWidth}
                        onChange={(e) => setRawInput(prev => ({...prev, laneWidth: e.target.value}))}
                        onBlur={(e) => setCalculationInput(prev => ({...prev, laneWidth: e.target.value}))}
                    />
                    <Button
                        prefix={<MaterialIcon name='content_paste' weight={300}/>}
                        text="Lim inn horisontaldata"
                        type="secondary"
                        onClick={pasteHandler}
                    />
                    
                </ToolsContainer>
                <ToolsContainer title='Faste variabler'>
                    <Input
                        title="Hjulavstand"
                        prefix={<span>b</span>}
                        sufix='m'
                        value={calculatedState.derived?.wheelbase?.value?.toFixed(3) ?? ''}
                        disabled
                    />
                    <Input
                        title="Relativ vertikalfart"
                        prefix={<span>v<sub>vf</sub></span>}
                        sufix='m/m'
                        value={calculatedState.derived?.relativeVerticalSpeed?.value?.toFixed(3) ?? ''}
                        disabled
                    />
                    <Input
                        title="Fartstillegg"
                        prefix={<span>∆ v<sub>t</sub></span>}
                        sufix='km/t'
                        value={calculatedState.derived?.speedAddition?.value?.toFixed(3) ?? ''}
                        disabled
                    />
                    <Input
                        title="Tverrfall"
                        prefix={<span>e</span>}
                        sufix='m/m'
                        value={calculatedState.derived?.crossSlope?.value?.toFixed(3) ?? ''}
                        disabled
                    />
                </ToolsContainer>
            </ToolsColumn>
            <ToolsColumn className="outputColumn">
                {
                    calculatedState.output?.crossSlopeData?.valid
                    && Array.isArray(calculatedState.output?.crossSlopeData?.value.crossSlopeLeftSide)
                    && calculatedState.output?.crossSlopeData?.value.crossSlopeLeftSide.length !== 0 ? 
                    <ToolsContainer title="Tverrfall venstre side">
                        <Table
                            headers={[
                                {key: 'stationing', label: <span>Profil</span>},
                                {key: 'crossSlope', label: <span>Helning</span>},
                            ]}
                            data={calculatedState.output.crossSlopeData.value.crossSlopeLeftSide}
                        />
                        <Button
                            type='secondary'
                            prefix={<MaterialIcon name='content_copy' weight={300}/>}
                            text={'Kopier til utklipstavle'}
                            onClick={() => copyToClipboard(calculatedState.output.crossSlopeData.value.crossSlopeLeftSide)}
                        />
                    </ToolsContainer>
                    : null
                }
                {
                    calculatedState.output?.crossSlopeData?.valid
                    && Array.isArray(calculatedState.output?.crossSlopeData?.value.crossSlopeRightSide)
                    && calculatedState.output?.crossSlopeData?.value.crossSlopeRightSide.length !== 0 ? 
                    <ToolsContainer title="Tverrfall høyre side">
                        <Table
                            headers={[
                                {key: 'stationing', label: <span>Profil</span>},
                                {key: 'crossSlope', label: <span>Helning</span>},
                            ]}
                            data={calculatedState.output.crossSlopeData.value.crossSlopeRightSide}
                            errorKey={'error'}
                        />
                        <Button
                            type='secondary'
                            prefix={<MaterialIcon name='content_copy' weight={300}/>}
                            text={'Kopier til utklipstavle'}
                            onClick={() => copyToClipboard(calculatedState.output.crossSlopeData.value.crossSlopeRightSide)}
                        />
                    </ToolsContainer>
                    : null
                }
            </ToolsColumn>
            <ToolsColumn className="infoColumn">
                {
                    calculatedState.input?.designClass?.valid ?
                    <ToolsContainer title="Dimensjoneringstabell">
                        <Table
                            headers={[
                                {key: 'minRh', label: <span>R<sub>h,min</sub></span>},
                                {key: 'minA', label: <span>A<sub>min</sub></span>},
                                {key: 'minRvCrest', label: <span>R<sub>v,høy,min</sub></span>},
                                {key: 'minRvSag', label: <span>R<sub>v,lav,min</sub></span>},
                                {key: 'e', label: <span>e</span>, unit: '[m/m]'},
                                {key: 's', label: <span>s</span>, unit: '[m/m]'},
                            ]}
                            data={calculatedState.input.designClass.value.designTable}
                        />
                    </ToolsContainer>
                    : null
                }
                {
                    calculatedState.input?.horizontalGeometry?.valid ? 
                    <ToolsContainer title='Importert horisontalgeometri'>
                        <Table
                            headers={[
                                {key: 'type', label: <span>Element</span>},
                                {key: 'startStationing', label: <span>Startprofil</span>, unit: '[m]'},
                                {key: 'mergedRadius', label: <span>Radius</span>, unit: '[m]'},
                                {key: 'clothoideParameter', label: <span>Parameter</span>, unit: '[m]'},
                                {key: 'directionChange', label: <span>Retningsendring</span>, unit: '[°]'},
                                {key: 'length', label: <span>Lengde</span>, unit: '[m]'},
                            ]}
                            data={calculatedState.input.horizontalGeometry.value}
                        />
                    </ToolsContainer>
                    : null
                }
                {
                    calculatedState.output?.crossSlopeData?.valid
                    && Array.isArray(calculatedState.output?.crossSlopeData?.value.crossSlopeLeftSide)
                    && calculatedState.output?.crossSlopeData?.value.crossSlopeLeftSide.length !== 0
                    && Array.isArray(calculatedState.output?.crossSlopeData?.value.crossSlopeRightSide)
                    && calculatedState.output?.crossSlopeData?.value.crossSlopeRightSide.length !== 0 ? 
                    <ToolsContainer title="Tverrfall venstre side">
                        <div className="graphContainer">
                            <ResponsiveContainer
                                height='100%'
                                width='100%'
                                minWidth='400px'
                                minHeight='0'
                                aspect={undefined}
                            >
                                <LineChart margin={{top: 10, left: 30, right: 30, bottom: 0}}>
                                    <Legend verticalAlign="bottom" layout="vertical"/>
                                    <YAxis
                                        type='number'
                                        dataKey='crossSlope'
                                        //xAxisId={'stationing'}
                                        fontSize={12}
                                        ticks={YAxisTicks}
                                        domain={[-0.13, 0.13]}
                                        label={{
                                            angle: -90,
                                            value: 'Tverrfall [m/m]',
                                            position: 'center',
                                            dx: -15,
                                            style: {
                                                textAnchor: 'middle',
                                            },
                                        }}
                                    />
                                    <XAxis
                                        type='number'
                                        height={50}
                                        dataKey='stationing'
                                        fontSize={12}
                                        ticks={XAxisTicks}
                                        domain={['dataMin', 'dataMax']}
                                        label={{
                                            value: 'Profil [m]',
                                            position: 'center',
                                            dy: 10,
                                            style: {
                                                textAnchor: 'middle',
                                            },
                                        }}
                                    />
                                    <CartesianGrid
                                        strokeDasharray={'3 3'}
                                    />
                                    <Line
                                        data={calculatedState.output?.crossSlopeData?.value.crossSlopeLeftSide}
                                        name='Tverrfall venstre side'
                                        dataKey={'crossSlope'}
                                        dot={false}
                                        strokeWidth={2}
                                        stroke={GlobalTheme.colors.successForeground}
                                        activeDot={false}
                                    />
                                    <Line
                                        data={calculatedState.output?.crossSlopeData?.value.crossSlopeRightSide}
                                        name='Tverrfall høyre side'
                                        dataKey={'crossSlope'}
                                        dot={false}
                                        strokeWidth={2}
                                        stroke={GlobalTheme.colors.warningForeground}
                                        activeDot={false}
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </ToolsContainer>
                    : null
                }
            </ToolsColumn>
        </StyledSuperelevation>
    )
}

const StyledSuperelevation = styled.div`
    flex: 1;
    width: 100%;

    display: grid;
    flex: 1;
    width: 100%;
    min-height: 0;

    @media (min-width: ${({theme}) => theme.breakPoints.xl})
    {
        grid-template-columns: 1fr 1fr 2fr;
        grid-template-rows: 1fr;
        overflow: hidden;

        > div
        {
            
            min-height: 0;
        }
    }

    @media (max-width: ${({theme}) => theme.breakPoints.xl})
    {
        grid-template-columns: 1fr 1fr;
        grid-template-rows: min-content min-content;

        div.inputColumn, div.outputColumn
        {
            //max-height: 500px;
        }
        div.infoColumn
        {
            //max-height: 0.5fr;
            grid-column: 1/3;
        }
    }

    @media (max-width: ${({theme}) => theme.breakPoints.md})
    {
        grid-template-columns: 1fr;
        grid-template-rows: min-content min-content min-content;

        div.inputColumn
        {
            grid-column: 1;
            grid-row: 1/2;
        }
        div.outputColumn
        {
            grid-column: 1;
            grid-row: 2/3;
        }
        div.infoColumn
        {
            grid-column: 1;
            grid-row: 3/4;
        }
    }

    div.graphContainer
    {
        min-height: 300px;
        overflow: auto;
    }
`